"use client";

import React from "react";
import {
  Box,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  HStack,
  useToast,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
} from "@chakra-ui/react";
import { MoreVertical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppTheme } from "../ThemeContext.tsx";
import Layout from "./Layout.tsx";
import {
  agentService,
  type Agent,
  type VoiceDetails,
  type LLMData,
} from "../services/agentService.ts";
import { phoneNumberService } from "../services/phoneNumberService.ts";
import GenderImage from "./agents/voices/GenderImages.tsx";
import { AgentTypeDropdown } from "./agents/AgentTypeDropdown.tsx";

const Agents = () => {
  const [agents, setAgents] = React.useState<Agent[]>([]);
  const [phoneNumbers, setPhoneNumbers] = React.useState<
    Record<string, string[]>
  >({});
  const [voiceDetails, setVoiceDetails] = React.useState<
    Record<string, VoiceDetails>
  >({});
  const [llmData, setLlmData] = React.useState<Record<string, LLMData>>({});
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(true);

  const toast = useToast();
  const navigate = useNavigate();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const loadAgents = React.useCallback(async () => {
    try {
      const data = await agentService.list();
      setAgents(data);

      // Fetch voice details for each agent
      const voicePromises = data.map((agent) =>
        agentService.getVoiceDetails(agent.voice_id)
      );
      const voiceResults = await Promise.all(voicePromises);
      const voiceMap: Record<string, VoiceDetails> = {};
      voiceResults.forEach((voice, index) => {
        voiceMap[data[index].voice_id] = voice;
      });
      setVoiceDetails(voiceMap);

      // Fetch LLM data for each agent with retell-llm type
      const llmPromises = data
        .filter(
          (agent) =>
            agent.response_engine.type === "retell-llm" &&
            agent.response_engine.llm_id
        )
        .map((agent) => agentService.getLLMData(agent.response_engine.llm_id));

      const llmResults = await Promise.all(llmPromises);
      const llmMap: Record<string, LLMData> = {};

      data.forEach((agent, index) => {
        if (
          agent.response_engine.type === "retell-llm" &&
          agent.response_engine.llm_id
        ) {
          const matchingLlm = llmResults.find(
            (llm) => llm.llm_id === agent.response_engine.llm_id
          );
          if (matchingLlm) {
            llmMap[agent.response_engine.llm_id] = matchingLlm;
          }
        }
      });

      setLlmData(llmMap);
    } catch (error) {
      console.error("Error loading agents:", error);
      toast({
        title: "Error loading agents",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const loadPhoneNumbers = React.useCallback(async () => {
    try {
      const numbers = await phoneNumberService.list();
      const agentPhoneMap: Record<string, string[]> = {};

      numbers.forEach((number) => {
        if (number.inbound_agent_id) {
          if (!agentPhoneMap[number.inbound_agent_id]) {
            agentPhoneMap[number.inbound_agent_id] = [];
          }
          agentPhoneMap[number.inbound_agent_id].push(
            number.phone_number_pretty
          );
        }
        if (number.outbound_agent_id) {
          if (!agentPhoneMap[number.outbound_agent_id]) {
            agentPhoneMap[number.outbound_agent_id] = [];
          }
          agentPhoneMap[number.outbound_agent_id].push(
            number.phone_number_pretty
          );
        }
      });

      setPhoneNumbers(agentPhoneMap);
    } catch (error) {
      console.error("Error loading phone numbers:", error);
    }
  }, []);

  React.useEffect(() => {
    Promise.all([loadAgents(), loadPhoneNumbers()]);
  }, [loadAgents, loadPhoneNumbers]);

  const handleDeleteAgent = async (agentId: string) => {
    try {
      await agentService.delete(agentId);
      toast({
        title: "Agent deleted successfully",
        status: "success",
        duration: 3000,
      });
      // Reload agents after delete
      loadAgents();
    } catch (error) {
      toast({
        title: "Error deleting agent",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleAgentCreated = () => {
    loadAgents();
  };

  const filteredAgents = agents.filter((agent) =>
    (agent.agent_name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <Layout>
        <Flex justify="center" align="center" h="100vh" w="100%">
          <Spinner size="xl" />
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box p={6}>
        {/* Top Bar with Heading and Create Button */}
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" mb={{ base: 2, md: 0 }}>
            Agents
          </Heading>
          <HStack spacing={2}>
            <AgentTypeDropdown onAgentCreated={handleAgentCreated} />
          </HStack>
        </Flex>

        {filteredAgents.length === 0 ? (
          <Alert
            status="info"
            flexDirection="column"
            alignItems="center"
            bgColor="transparent"
            marginTop={20}
          >
            <AlertIcon color={isDark ? "white" : "black"} />
            <AlertTitle>No Agents Found!</AlertTitle>
            <AlertDescription color={isDark ? "gray.200" : "gray.500"}>
              Create an agent to get started.
            </AlertDescription>
          </Alert>
        ) : (
          <Box
            overflowX="auto"
            bg="transparent"
            width="100%"
            sx={
              isDark
                ? {
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#2D3748", // Dark track color
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#4A5568", // Dark thumb color
                      borderRadius: "2px",
                    },
                    // For Firefox
                    scrollbarColor: "#4A5568 #2D3748", // thumb, then track
                    _hover: {
                      scrollbarColor: "#4A5568 #2D3748", // same colors on hover
                    },
                  }
                : {
                    "&::-webkit-scrollbar": {
                      height: "4px",
                    },
                    "&::-webkit-scrollbar-button": {
                      display: "none",
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#fff",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#fff",
                      borderRadius: "2px",
                    },
                    scrollbarColor: "rgb(220, 220, 220) white",
                    _hover: {
                      scrollbarColor: "rgb(220, 220, 220) white",
                    },
                  }
            }
          >
            <Table colorScheme={isDark ? "whiteAlpha" : "blackAlpha"}>
              <Thead>
                <Tr>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Agent Name</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Agent Type</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Voice</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Phone</Th>
                  <Th color={isDark ? "gray.300" : "gray.700"}>Edited by</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredAgents.map((agent) => {
                  const voice = voiceDetails[agent.voice_id];
                  const agentPhones = phoneNumbers[agent.agent_id] || [];

                  // Determine if this is a multi-prompt agent
                  let agentType = agent.response_engine.type;
                  if (agentType === "retell-llm") {
                    const llmId = agent.response_engine.llm_id;
                    const llm = llmData[llmId];
                    agentType =
                      llm && Array.isArray(llm.states)
                        ? "Multi-Prompt"
                        : "Single-Prompt";
                  }

                  return (
                    <Tr
                      key={agent.agent_id}
                      cursor="pointer"
                      onClick={() => navigate(`/agents/${agent.agent_id}`)}
                      _hover={{ bg: isDark ? "gray.700" : "gray.300" }}
                      bgColor={isDark ? "gray.800" : "whiteAlpha"}
                    >
                      <Td _hover={{ color: "gray.500" }}>
                        <HStack spacing={3}>
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g id="robot-2-line">
                              <path
                                id="Vector"
                                d="M12.9248 4.29999C12.9248 4.63318 12.78 4.93253 12.5498 5.13853V6.54999H16.2998C17.5425 6.54999 18.5498 7.55735 18.5498 8.79999V16.3C18.5498 17.5427 17.5425 18.55 16.2998 18.55H7.2998C6.05717 18.55 5.0498 17.5427 5.0498 16.3V8.79999C5.0498 7.55735 6.05717 6.54999 7.2998 6.54999H11.0498V5.13853C10.8196 4.93253 10.6748 4.63318 10.6748 4.29999C10.6748 3.67867 11.1785 3.17499 11.7998 3.17499C12.4211 3.17499 12.9248 3.67867 12.9248 4.29999ZM7.2998 8.04999C6.88559 8.04999 6.5498 8.38578 6.5498 8.79999V16.3C6.5498 16.7142 6.88559 17.05 7.2998 17.05H16.2998C16.714 17.05 17.0498 16.7142 17.0498 16.3V8.79999C17.0498 8.38578 16.714 8.04999 16.2998 8.04999H12.5498H11.0498H7.2998ZM4.2998 10.3H2.7998V14.8H4.2998V10.3ZM19.2998 10.3H20.7998V14.8H19.2998V10.3ZM9.5498 13.675C10.1711 13.675 10.6748 13.1713 10.6748 12.55C10.6748 11.9287 10.1711 11.425 9.5498 11.425C8.92848 11.425 8.4248 11.9287 8.4248 12.55C8.4248 13.1713 8.92848 13.675 9.5498 13.675ZM14.0498 13.675C14.6711 13.675 15.1748 13.1713 15.1748 12.55C15.1748 11.9287 14.6711 11.425 14.0498 11.425C13.4285 11.425 12.9248 11.9287 12.9248 12.55C12.9248 13.1713 13.4285 13.675 14.0498 13.675Z"
                                fill="currentColor"
                              ></path>
                            </g>
                          </svg>
                          <Text>{agent.agent_name || "Unnamed Agent"}</Text>
                        </HStack>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme="gray"
                          background={isDark ? "gray.700" : "gray.200"}
                          color={isDark ? "whiteAlpha" : "gray.800"}
                        >
                          {agentType}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <GenderImage voice={voice} isDark={isDark} />
                          <Text>{voice?.voice_name || "Unknown Voice"}</Text>
                        </HStack>
                      </Td>
                      <Td>
                        {agentPhones.length > 0 ? (
                          <HStack>
                            <Text>{agentPhones[0]}</Text>
                            {agentPhones.length > 1 && (
                              <Tooltip
                                label={agentPhones.slice(1).join(", ")}
                                hasArrow
                              >
                                <Badge
                                  colorScheme="gray"
                                  bgColor={isDark ? "gray.700" : "gray.300"}
                                  color={isDark ? "whiteAlpha" : "gray.800"}
                                  rounded="lg"
                                >
                                  +{agentPhones.length - 1}
                                </Badge>
                              </Tooltip>
                            )}
                          </HStack>
                        ) : (
                          "-"
                        )}
                      </Td>
                      <Td>
                        {new Date(
                          agent.last_modification_timestamp
                        ).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<MoreVertical size={16} />}
                            variant="ghost"
                            size="sm"
                            color={isDark ? "white" : "blackAlpha"}
                          />
                          <MenuList bgColor={isDark ? "blackAlpha" : "white"}>
                            <MenuItem
                              onClick={() => handleDeleteAgent(agent.agent_id)}
                              bgColor={isDark ? "blackAlpha" : "white"}
                              _hover={{ bg: isDark ? "gray.500" : "gray.300" }}
                            >
                              Delete
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </Layout>
  );
};

export default Agents;
